import * as Sentry from '@sentry/react'
import env from './env'
import { __DEV__ } from '@clara/utils'
import dsns from './sentry-dsns'

const EXTRA_KEY = 'ROUTE_TO'

const getFirstPathSegment = (event: Sentry.Event): string | null => {
  const url = event?.request?.url
  if (!url) return null
  try {
    const urlObj = new URL(url)
    const pathParts = urlObj.pathname.split('/').filter(Boolean)
    return pathParts.length > 0 ? pathParts[0] : null
  } catch (error) {
    return null
  }
}

const transport = Sentry.makeMultiplexedTransport(
  Sentry.makeFetchTransport,
  (args) => {
    const event = args.getEvent()
    const team = event?.tags?.team
    const pathTeam = getFirstPathSegment(event!)

    if (team && typeof team === 'string' && dsns[team]) {
      console.log('[Custom Event]Issue inside transport', team)
      return [{ dsn: dsns[team], release: `${team}@1.0.0` }]
    } else if (pathTeam) {
      console.log('[Untracking]Issue inside transport', pathTeam)
      return [{ dsn: dsns[pathTeam], release: `${pathTeam}@1.0.0` }]
    } else {
      return []
    }
  },
)

if (env.SENTRY_ENABLED === 'true') {
  Sentry.init({
    dsn: env.REACT_APP_SENTRY_DSN_SHELL,
    allowUrls: [
      'localhost', // fix: remove this after testing
      'app.clara.cc',
      'colombia.clara.com',
      'brasil.clara.com',
      'frontend.clara-staging.tools',
    ],
    denyUrls: [
      // Filtrar URLs específicas que causan errores no relevantes
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /extensions\//i,
      /^safari-extension:\/\//i,
      /^resource:\/\//i,
      /^file:\/\//i,
      // Regexp para módulos de node_modules
      /\/node_modules\//i,
    ],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.moduleMetadataIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost', // fix: remove this after testing
      'app.clara.cc',
      'colombia.clara.com',
      'brasil.clara.com',
      'frontend.clara-staging.tools',
    ],
    transport,
    beforeSend: (event, hint) => {
      const exception = hint?.originalException
      if (exception && event?.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames

        // Validate if all frames are external (not our code)
        const isExternalError = frames.every(
          (frame) =>
            frame.filename &&
            (frame.filename.includes('node_modules') || !frame.in_app),
        )

        if (isExternalError) {
          return null
        }

        // Find the last frame with module metadata containing a DSN
        const routeTo = frames
          .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
          .map((v) => v.module_metadata)
          .slice(-1) // using top frame only - you may want to customize this according to your needs

        if (routeTo.length) {
          event.extra = {
            ...event.extra,
            [EXTRA_KEY]: routeTo,
          }
        }
      }

      return event
    },
  })
}

declare global {
  interface Window {
    __sentry__: typeof Sentry
  }
}

if (env.SENTRY_ENABLED === 'true') {
  window.__sentry__ = Sentry
}
